'use client';

import { useState } from 'react';

import { mapLayers, additionalLayers } from '../markupConfigs';

import { AppImageClient, AppImageData } from '@/shared/components/appImage';
import { PageSection } from '@/shared/components/pageSection';
import { Switch } from '@/shared/components/switch/ui/Switch';
import { Tooltiped } from '@/shared/components/tooltiped';

interface MapLayersSectionProps {
  pageTranslations: Record<string, string>;
  /** Данные AppImage по ключу слоя из `getAppImagesData`. */
  imagesDataMap: Record<string, AppImageData>;
}

export const MapLayersSection: React.FC<MapLayersSectionProps> = ({
  pageTranslations,
  imagesDataMap,
}) => {
  const [selectedLayers, setSelectedLayers] = useState<string[]>([
    '1',
    '2',
    '3',
    '4',
  ]);

  const handleLayerChange = (layer: string) => {
    if (selectedLayers.includes(layer)) {
      setSelectedLayers((prev) => prev.filter((l) => l !== layer));
    } else {
      setSelectedLayers((prev) => [...prev, layer]);
    }
  };

  const joinedLayers = [...mapLayers, ...additionalLayers];

  return (
    <PageSection
      title={pageTranslations.dataLayersHeader}
      titleId="dataLayersHeader"
      titleClassName="max-lg:max-w-[300px]"
      description={pageTranslations.dataLayersDescription}
      descriptionId="dataLayersDescription"
      descriptionClassName="max-lg:hidden"
      childrenWrapperClassName="px-4"
    >
      <div className="flex gap-[32px] max-lg:flex-col max-lg:gap-[20px]">
        <div className="flex w-[468px] flex-col gap-[32px] max-lg:order-2 max-lg:w-full max-lg:flex-none max-lg:gap-[20px]">
          <div className="bg-surface-base-secondary w-full rounded-[20px] p-[32px] max-lg:px-[20px] max-lg:py-[24px]">
            <p
              id="dataLayersBaselayersTitle"
              className="text-text-base-primary mb-[20px] text-[24px] leading-[32px] font-medium max-lg:mb-[16px] max-lg:text-[20px] max-lg:leading-[24px]"
            >
              {pageTranslations.dataLayersBaselayersTitle}
            </p>

            <div className="flex flex-col gap-[16px]">
              {mapLayers.map((layer) => (
                <div
                  className="flex gap-[16px] max-lg:gap-[8px]"
                  key={layer.value}
                  aria-label={layer.value}
                >
                  <Switch
                    checked={selectedLayers.includes(layer.value)}
                    onChange={() => handleLayerChange(layer.value)}
                    id={layer.value}
                  />

                  <p
                    id={layer.titleKey}
                    className="text-text-base-primary text-[16px] leading-[24px] font-normal max-lg:text-[14px] max-lg:leading-[20px]"
                  >
                    {pageTranslations[layer.titleKey]}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface-base-secondary w-full rounded-[20px] p-[32px]">
            <p
              id="dataLayersAdditionallayersTitle"
              className="text-text-base-primary mb-[20px] text-[24px] leading-[32px] font-medium"
            >
              {pageTranslations.dataLayersAdditionallayersTitle}
            </p>

            <div className="flex flex-col gap-[16px]">
              {additionalLayers.map((layer) => (
                <div
                  className="flex gap-[16px] max-lg:gap-[8px]"
                  key={layer.value}
                >
                  <Switch
                    checked={selectedLayers.includes(layer.value)}
                    onChange={() => handleLayerChange(layer.value)}
                  />

                  <div className="text-text-base-primary text-[16px] leading-[24px] font-normal max-lg:text-[14px] max-lg:leading-[20px]">
                    {!!pageTranslations[layer.tooltipContentKey] && (
                      <Tooltiped
                        tooltipContent={
                          <div id={layer.tooltipContentKey}>
                            {pageTranslations[layer.tooltipContentKey]}
                          </div>
                        }
                        className="max-lg:h-none bg-custom-surface-contrast-low flex min-h-[24px] items-center rounded-[7px] border border-[#FFFFFF1A] px-[7px] max-lg:min-h-[24px] max-lg:rounded-[8px] max-lg:px-[6px] max-lg:pb-0"
                      >
                        <span
                          id={layer.titleKey}
                          className="text-text-base-primary text-[16px] leading-[24px] font-normal max-lg:text-[14px] max-lg:leading-[20px]"
                        >
                          {pageTranslations[layer.titleKey]}
                        </span>
                      </Tooltiped>
                    )}

                    {!pageTranslations[layer.tooltipContentKey] && (
                      <span className="text-text-base-primary text-[16px] leading-[24px] font-normal max-lg:text-[14px] max-lg:leading-[20px]">
                        {pageTranslations[layer.titleKey]}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="relative h-[552px] w-[668px] flex-1 overflow-hidden rounded-[20px] bg-[#f6f3ed] max-lg:order-1 max-lg:h-[288px] max-lg:w-full max-lg:flex-none">
          {joinedLayers.map((layer) => (
            <AppImageClient
              key={layer.value}
              {...imagesDataMap[layer.image]}
              alt={pageTranslations[layer.titleKey]}
              fill
              className="object-cover"
              style={{
                opacity: selectedLayers.includes(layer.value) ? 1 : 0,
              }}
            />
          ))}
        </div>
      </div>
    </PageSection>
  );
};
